import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { listLotAllocationsApi } from 'api/inventory';
import useRoles from 'common/hooks/useRoles';
import { loadReversalQueuePage } from 'store/thunks/reversalQueueThunks';
import ReversalReviewFilterButton from './components/ReversalReviewFilterButton';
import ReversalReviewFilterStatusBar from './components/ReversalReviewFilterStatusBar';
import ReversalReviewTable from './components/ReversalReviewTable';
import ResolveAllocationDialog from './components/ResolveAllocationDialog';

export const usePendingReversalsCount = () => {
  const [count, setCount] = useState(0);
  const { viewReversalReview } = useRoles();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    // listLotAllocations is shielded to Inventory Admin / Manager / POM Admin. An Inventory
    // Viewer reaches this module but not the Reversal Review tab, so without this gate every
    // Inventory page load by one of them spends a request on a denial the catch swallows,
    // for a badge that is never rendered.
    if (!viewReversalReview) {
      setCount(0);
      return Promise.resolve();
    }

    // POM-6228 - taggedForReview on its own IS the pending set. This used to also pin
    // status to IN_STOCK, which no code path ever writes (WIP on create, DISPENSED on ship,
    // RESOLVED on settle), so the badge was structurally zero. Resolution clears the tag in
    // the same statement that writes RESOLVED and only once the allocation is fully
    // resolved, so a part-resolved row correctly stays counted.
    return listLotAllocationsApi({ filter: { taggedForReview: true }, limit: 1, offset: 0 })
      .then((data) => {
        if (isMounted.current) setCount(data?.total ?? 0);
      })
      .catch(() => {
        if (isMounted.current) setCount(0);
      });
  }, [viewReversalReview]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { count, refresh };
};

const ManageReversals = () => {
  const dispatch = useDispatch();
  const [selectedRow, setSelectedRow] = useState(null);
  // Supplied by InventoryManagement, which owns the tab badge. Absent when this screen is
  // rendered on its own (tests), hence the guard rather than a required context.
  const { refreshPendingCount } = useOutletContext() ?? {};

  const handleManage = (row) => setSelectedRow(row);

  const handleClose = () => setSelectedRow(null);

  const handleSuccess = () => {
    setSelectedRow(null);
    dispatch(loadReversalQueuePage({ page: 1 }));
    // The badge is owned by a component that stays mounted across tab changes, so without
    // this it would hold its pre-resolution number until a full page reload.
    refreshPendingCount?.();
  };

  return (
    <Box sx={{ px: 3, pt: 2, pb: 2 }}>
      <Grid container direction='column'>
        <Grid item container alignItems='center' sx={{ py: 1 }}>
          <Grid item sx={{ flexGrow: 1 }}>
            <ReversalReviewFilterStatusBar />
          </Grid>
          <Grid item>
            <ReversalReviewFilterButton />
          </Grid>
        </Grid>

        <Grid item container sx={{ pt: 2 }}>
          <ReversalReviewTable onManage={handleManage} />
        </Grid>
      </Grid>

      <ResolveAllocationDialog
        open={Boolean(selectedRow)}
        row={selectedRow}
        onClose={handleClose}
        onSuccess={handleSuccess}
      />
    </Box>
  );
};

export default ManageReversals;
